import { getOperationName } from '@apollo/client/utilities';
import { Box, Chip, Stack, Tooltip } from '@cmg/design-system';
import isEmpty from 'lodash/isEmpty';
import React, { useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';

import routeFactory from '../../../../../../common/util/routeFactory';
import { useIsUsFiledAndNotSellSideConsolidatedCalendar } from '../../../../../calendar/hooks/useIsUsFiledAndNotSellSideConsolidatedCalendar';
import { useCheckIsFollowingOffering } from '../../../../../shared/follow-offering/useCheckIsFollowingOffering';
import useFollowOfferingMutation from '../../../../../shared/follow-offering/useFollowOfferingMutation';
import type { Calendar_UserOfferingPartsFragment } from '../../../../graphql/__generated__/Calendar_GridRowData';
import { Calendar_GridRowDataDocument } from '../../../../graphql/__generated__/Calendar_GridRowData';
import type { CalendarGridCustomCellRendererProps } from '../../CalendarGrid.model';
import { getIssuerChips } from './IssuerCellRenderer.model';
import { IssuerFollowButton } from './IssuerFollowButton';

export type IssuerCellRendererProps = CalendarGridCustomCellRendererProps<never>;

const EMPTY_ARRAY = [];
export const IssuerCellRenderer: React.FC<IssuerCellRendererProps> = ({ data }) => {
  const href = routeFactory.offerings.getUrlPath({ id: data?.id });
  const userOfferings = (data?.userOfferings ?? EMPTY_ARRAY).filter(
    (offering): offering is Calendar_UserOfferingPartsFragment => !!offering
  );

  const chips = useMemo(() => getIssuerChips({ offering: data }), [data]);
  const isLinkDisabled = useIsUsFiledAndNotSellSideConsolidatedCalendar(data ?? null);
  const isFollowing = useCheckIsFollowingOffering({ userOfferings });

  const { setFollowingOffering } = useFollowOfferingMutation({
    refetchQueries: [getOperationName(Calendar_GridRowDataDocument)!],
  });

  const handleFollowButtonClick = useCallback(
    (isFollowing: boolean) => data?.id && setFollowingOffering(data?.id, isFollowing),
    [data?.id, setFollowingOffering]
  );

  return (
    <Stack flexDirection="row" gap={1} alignItems="center">
      <IssuerFollowButton onClick={handleFollowButtonClick} isFollowing={isFollowing} />

      <Box overflow="hidden" textOverflow="ellipsis" minWidth={10}>
        {isLinkDisabled ? data?.issuer?.name : <Link to={href}>{data?.issuer?.name}</Link>}
      </Box>

      {!isEmpty(chips) && (
        <Stack
          gap={0.5}
          flexDirection="row"
          alignItems="center"
          justifyContent="flex-end"
          flexGrow={1}
          flexShrink={0}
        >
          {chips.map(({ tooltip, ...chipProps }, index) => (
            <Tooltip key={index} title={tooltip} variant="info">
              <Chip size="small" {...chipProps} />
            </Tooltip>
          ))}
        </Stack>
      )}
    </Stack>
  );
};
